/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  COMMENT,
  compile,
  DECLARATION,
  Element,
  IMPORT,
  RULESET,
} from 'stylis';
import { serializeStyles } from '@emotion/serialize';
import { merge } from 'lodash';
import { HEIGHT_UNITS } from '../../../types';
import { useCustomCSSEditor } from '../../../hooks/useCustomCSSEditor';
import { useThemesetPropertyOrDefault } from '../../themesets/ThemesetManager';

export function camelCaseToKebabCase(propertyName) {
  return propertyName.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase();
}

export const addToObj = (element: Element, obj: object) => {
  switch (element.type) {
    case DECLARATION:
      obj[camelCaseToKebabCase(element.props)] = element.children;
      return;
    case COMMENT:
      return;
    case RULESET: {
      if (!element.children.length) return;
      const props = element.props as string[];
      const key = props.join(',');
      obj[key] = serializeToObject(element.children);
      return;
    }
    case IMPORT:
      throw new Error('Not sure how to convert this properly to an object');
    default:
      if (!element.children.length) return;
      obj[element.value] = serializeToObject(element.children);
      return;
  }
};
export const serializeToObject = elements => {
  const obj = {};
  elements.forEach(element => addToObj(element, obj));
  return obj;
};

export function addNewLines(inputString: string) {
  // Add new line after every curly brace and semicolon
  const stringWithNewLines = inputString.replace(/([{};])/g, '$1\n');
  return stringWithNewLines;
}

export const updateStyles = (styles: string, newStyles: string) => {
  const newStylesObj = addCharToKeys(
    serializeToObject(compile(newStyles)),
    '&',
  );
  const stylesObj = addCharToKeys(serializeToObject(compile(styles)), '&');
  const stringStyle = serializeStyles([merge(stylesObj, newStylesObj)]).styles;
  return addNewLines(stringStyle);
};

export const deleteStyle = (styles: string, key: string, selector?: string) => {
  const stylesObj = addCharToKeys(serializeToObject(compile(styles)), '&');

  return addNewLines(
    serializeStyles([deleteKeyValuePair(stylesObj, key, selector)]).styles,
  );
};

export const getValueFromStyles = (
  styles: string,
  key: string,
  selector?: string,
) => {
  if (styles) {
    const stylesObj = serializeToObject(compile(styles));
    let value;
    if (!selector) {
      value = stylesObj[key];
    } else {
      value = stylesObj[selector]?.[key];
    }
    return value ?? null;
  }
  return null;
};

function addCharToKeys(
  obj: { [key: string]: any },
  char: string,
): { [key: string]: any } {
  const newObj: { [key: string]: any } = {};

  Object.keys(obj).forEach(key => {
    newObj[key.startsWith(':') ? `${char}${key}` : key] = obj[key];
  });

  return newObj;
}

function deleteKeyValuePair(
  obj: { [key: string]: any },
  keyToRemove: string,
  nestedKey?: string,
): { [key: string]: any } {
  if (nestedKey) {
    const nestedObj = obj[nestedKey];
    if (!nestedObj) {
      return obj;
    }
    const { [keyToRemove]: _, ...newNestedObj } = nestedObj;
    return {
      ...obj,
      [nestedKey]: newNestedObj,
    };
  }
  const { [keyToRemove]: _, ...newObj } = obj;

  return newObj;
}

export function removeDot(str: string): string {
  return str.replace('.', '');
}

export function splitValueAndUnit(
  input: string,
): [string, HEIGHT_UNITS | null] {
  const allowedUnits = [HEIGHT_UNITS.PX, HEIGHT_UNITS.VH];
  const regex = /^(\d+)(px|vh)$/;

  if (typeof input === 'string') {
    const result = input.match(regex);
    if (result && allowedUnits.includes(result[2] as HEIGHT_UNITS)) {
      return [result[1], result[2] as HEIGHT_UNITS];
    }
    // If not a number+unit, return the string and null
    return [input, null];
  }
  return [input, null];
}

export const useWidgetStyleProperty = (
  widgetStyle: string,
  themesetPropertyPath: string,
  inspectorPropertyPath: string,
  inspectorPropertySelector?: string,
): {
  value: string;
  origin: 'inspector' | 'themeset' | 'default' | 'unknown';
} => {
  const { getValueFromStyle } = useCustomCSSEditor(widgetStyle);
  const inspectorProperty = getValueFromStyle(
    inspectorPropertyPath,
    inspectorPropertySelector,
  );

  const { value: themesetProperty, origin } =
    useThemesetPropertyOrDefault(themesetPropertyPath);

  // Priority for properties:
  // 1 - Inspector property
  // 2 - Themeset property
  // 3 - Default value from themeset property
  if (inspectorProperty) {
    return {
      value: String(splitValueAndUnit(inspectorProperty)[0]),
      origin: 'inspector',
    };
  }
  return {
    value: String(themesetProperty),
    origin: origin,
  };
};

/**
 * Adds opening and closing double quotes to a string if it doesn't already have quotes
 * @param value The string to process
 * @returns String with quotes added or the original string if it already has quotes
 */
export function addQuotes(value: string): string {
  // Return early if value is falsy
  if (!value) return value;

  // Check if the string already starts and ends with quotes (single or double)
  const hasQuotes =
    (value.startsWith('"') && value.endsWith('"')) ||
    (value.startsWith("'") && value.endsWith("'"));

  // If it already has quotes, return it unchanged
  if (hasQuotes) {
    return value;
  }

  // Otherwise, add double quotes
  return `"${value}"`;
}

/**
 * Strips opening and closing quotation marks from a string
 * @param value The string to process
 * @returns String without surrounding quotation marks
 */
export function stripQuotes(value: string): string {
  // Return early if value is falsy
  if (!value) return value;

  // Remove leading and trailing quotes (both single and double)
  return value.replace(/^["']|["']$/g, '');
}

/**
 * Converts a value to a pixel string
 * Adds 'px' suffix to numbers or strings that don't already end with 'px'
 * 
 * @param value Number or string to convert to pixel string
 * @returns Formatted pixel string or original value if not a number or string
 */
export function toPx(value: number | string | null | undefined): string {
  // Return empty string for null/undefined
  if (value == null) return '';

  // Convert to string if it's a number
  const stringValue = typeof value === 'number' ? String(value) : value;

  // If it's not a string at this point, return empty string
  if (typeof stringValue !== 'string') return '';

  // If it's an empty string, return it
  if (stringValue === '') return '';

  // If it already ends with 'px', return as is
  if (stringValue.endsWith('px')) return stringValue;

  // Otherwise, append 'px'
  return `${stringValue}px`;
}